import { AfterViewInit, Component, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { MatIconModule } from '@angular/material/icon';
import { DataBindingDirective, ExcelModule, GridModule, RowClassArgs } from "@progress/kendo-angular-grid";
import { ToolBarModule } from "@progress/kendo-angular-toolbar";
import { InputsModule } from '@progress/kendo-angular-inputs';
import { chevronDownIcon, chevronUpIcon, fileExcelIcon, filterIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { process, SortDescriptor } from "@progress/kendo-data-query";
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UtilitiesService } from '../../../services/utilities.service';
import { getCurrentDateTime } from '../../../utilities/date-time';
import { IChatSession, IMessage } from '../../../interfaces/i-chat-session';
import { ApiService } from '../../../services/api.service';
import { ProfileService } from '../../../services/profile.service';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { ChatSessionService } from '../services/chat-session.service';
import { KENDO_GRID_PAGE_SIZES } from '../../../constants/api.constant';
import { AlertService } from '../../../shared/alert/alert.service';
import { AlertEnum } from '../../../shared/alert/alert.enum';
import { SensitivityLabelsComponent } from "../../../shared/sensitivity-labels/sensitivity-labels.component";
import { AiModelsComponent } from "../../../shared/ai-models/ai-models.component";
import { DialogConfirmComponent } from "../../../shared/dialog-confirm/dialog-confirm.component";
import { ISensitivityLabel, ISensitivityLabelExpanded } from "../../../interfaces/i-sensitivity-label";
import { IOpenAiModel } from "../../../interfaces/i-open-ai-model";
import { AgentService } from "../../agent/services/agent.service";
import { DEFAULT_SENSITIVITY_LABEL } from "../../../constants/sensitivity.constants";
import { mapLegacySensitivityLabel, toISensitivityLabel } from "../../../utilities/sensitivity-label.utils";
import { SensitivityLabelService } from "../../../services/sensitivity-label.service";
import { InfoButtonTooltipComponent } from '../../../shared/info-button-tooltip/info-button-tooltip.component';
import { environment } from '../../../../environments/environment';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IGrantedAgent } from "../../../interfaces/i-agents";

@Component({
  selector: 'app-session-manager',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    GridModule,
    ExcelModule,
    ToolBarModule,
    InputsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSliderModule,
    MatTabsModule,
    MatTooltipModule,
    SensitivityLabelsComponent,
    AiModelsComponent,
    InfoButtonTooltipComponent,
    KENDO_BUTTONS
],
  templateUrl: './session-manager.component.html',
  styleUrl: './session-manager.component.scss'
})
export class SessionManagerComponent implements OnInit, AfterViewInit {
  dataBinding = viewChild<DataBindingDirective>(DataBindingDirective);
  dialogTitle = viewChild<ElementRef>('dialogTitle');
  dialogContent = viewChild<ElementRef>('dialogContent');
  gridData: IGrantedAgent[] = [];
  gridView: IGrantedAgent[] = [];
  grantedAgents: IGrantedAgent[] = [];
  pageSizes = KENDO_GRID_PAGE_SIZES;
  agentSelections: string[] = [];
  errorMessage = signal('');
  data = inject(MAT_DIALOG_DATA);
  pageSize = 10;
  total = 0;
  temperature = 0.0;
  topP = 0.0;
  title = '';
  submitting = false;
  sensitivityLabelText = '';
  originalSensitivityLabel!: ISensitivityLabelExpanded;  // Used to check if the user has changed the Sensitivity Label
  sort: SortDescriptor[] = [];
  sortIcon: SVGIcon = filterIcon;
  activeTabIndex = 0; // 0 = Ai Config tab, 1 = Agents tab
  agentTooltipText = `Agents are automated processes (eg. APIs and microservices) that are registered for the AI model to utilize
              when handling requests. For example, the AI will ask the "Search Internet" agent to perform a web search based
              on a search criteria and return it to the AI. Upon which the AI will attempt to use that information to handle
              your request.`
  isCreateSession: boolean = false;

  readonly excelSVG: SVGIcon = fileExcelIcon;
  readonly sortAscSvg: SVGIcon = chevronUpIcon;
  readonly sortDescSvg: SVGIcon = chevronDownIcon;
  readonly sessionLabel = new FormControl(getCurrentDateTime(new Date()), [ Validators.required ]);
  readonly sensitivityLabel = new FormControl<ISensitivityLabelExpanded | null>(DEFAULT_SENSITIVITY_LABEL, [ Validators.required ]);
  readonly temperatureControl = new FormControl(0.5);
  readonly topPControl = new FormControl(0.5);
  readonly openAiModel = new FormControl<IOpenAiModel>({ model: environment.defaultModelConfig.model, accountName: environment.defaultModelConfig.accountName }, [Validators.required]);
  readonly dialog = inject(MatDialog);

  #id = crypto.randomUUID() as string;
  #messages: IMessage[] = [];
  #apiService = inject(ApiService);
  #profileService = inject(ProfileService);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);
  #chatSessionService = inject(ChatSessionService);
  #agentService = inject(AgentService);
  #utilitiesService = inject(UtilitiesService);
  #sensitivityLabelService = inject(SensitivityLabelService);

  /** Whether the sensitivity-label feature is enabled; label UI is hidden when disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  constructor(private dialogRef: MatDialogRef<SessionManagerComponent>) {
    this.title = this.data.action;
  }

  ngOnInit(): void {
    if (this.data.action === 'edit' || this.data.action === 'update') {
        this.#id = this.#chatSessionService.selectedSessionId().toString();
        this.agentSelections = this.#chatSessionService.selectedSession()?.agents ?? [];
        this.sessionLabel.setValue(this.#chatSessionService.selectedSession()?.label!);
        this.temperatureControl.setValue(this.#chatSessionService.selectedSession()?.temperature ?? this.temperature);
        this.topPControl.setValue(this.#chatSessionService.selectedSession()?.topP ?? this.topP);
        this.#messages = this.#chatSessionService.selectedSession()?.messages ?? [];
        this.openAiModel.setValue(this.#chatSessionService.selectedSession()?.model!);
        const sensitivityLabels = this.#chatSessionService.selectedSession()?.sensitivityLabels;
        if (sensitivityLabels && sensitivityLabels.length > 0) {
          this.sensitivityLabelText = this.#agentService.getSensitivityLabelString(sensitivityLabels[0]);
        }

      if (this.data.action === 'edit' && sensitivityLabels && sensitivityLabels.length > 0) {
        // Map from backend ISensitivityLabel → ISensitivityLabelExpanded for the form
        const expanded = mapLegacySensitivityLabel(sensitivityLabels[0]) ?? DEFAULT_SENSITIVITY_LABEL;
        this.originalSensitivityLabel = expanded;
        this.sensitivityLabel.setValue(expanded);
      }
    } else {
      this.isCreateSession = true;
    }
    if (this.data.action === 'update') {
      this.openNotificationDialog();
    }

    this.#loadGrantedAgentsBySensitivityLabel(this.sensitivityLabel.value!);
  }

  ngAfterViewInit() {
    // Focus the dialog title element after the view has been initialized
    const dialogTitleElement = this.dialogTitle();
    if (dialogTitleElement) {
      dialogTitleElement.nativeElement.focus();
    }

    // Scroll the mat-dialog-content to the top
    const dialogContentElement = this.dialogContent();
    if (dialogContentElement) {
      dialogContentElement.nativeElement.scrollTop = 0;
    }
  }

  public sortCheckboxColumn(): void {
    const isAscending = this.sort.length === 0 || this.sort[0].dir === 'desc';
    this.gridView = this.gridView.sort((a, b) => {
      const aSelected = this.agentSelections.includes(a.id);
      const bSelected = this.agentSelections.includes(b.id);

      if (aSelected && !bSelected) {
        return isAscending ? -1 : 1;
      } else if (!aSelected && bSelected) {
        this.sortIcon = isAscending ? this.sortAscSvg : this.sortDescSvg;
        return isAscending ? 1 : -1;
      } else {
        return 0;
      }
    });
    this.sort = [{ field: 'checkboxField', dir: isAscending ? 'asc' : 'desc' }];
    this.sortIcon = this.sort[0].dir === 'asc' ? this.sortAscSvg : this.sortDescSvg;
  }

  onSave() {
    this.#spinnerService.show();
    this.submitting = true;
    if (this.sessionLabel.value && this.sensitivityLabel.value?.id && this.openAiModel.value && this.openAiModel.value.model) {
      const defaultSession = JSON.parse(this.#profileService.userProfile()?.config?.defaultSession!);
      const regEx = /^[0-9a-zA-Z_\-.: ]+$/;
      const isValid = regEx.test(this.sessionLabel.value) && this.sessionLabel.value?.endsWith('.') === false
      const requestBody: IChatSession = {
        agents: (this.agentSelections.length > 0) ? this.#filterAgentsWithSensitivityLabelAsSession(this.sensitivityLabel.value!, this.agentSelections) : this.agentSelections,
        id: this.#id,
        isValid,
        label: this.sessionLabel.value!,
        messages: this.#messages,
        version: defaultSession.version,
        sensitivityLabels: [toISensitivityLabel(this.sensitivityLabel.value!)],
        temperature: this.temperatureControl.value!,
        topP: this.topPControl.value!,
        model: this.openAiModel.value!
      }


      this.#apiService.persistSession(requestBody).subscribe({
        next: resp => {
          const isLabelChanged = (this.originalSensitivityLabel) ? !this.compareLabels(this.sensitivityLabel.value!, this.originalSensitivityLabel) : undefined;
          // Now update the selected session
          this.#chatSessionService.chatSessions.update(sessions => {
            const sessionIndex = sessions?.findIndex(session => session.id === requestBody.id);
            if (sessionIndex !== undefined && sessionIndex !== -1) {
              sessions![sessionIndex] = requestBody;
            } else {
              sessions?.push(requestBody);
            }
            return sessions;
          });
          // for some reason, the computed() trigger doesn't seem to always be catching changes,
          // added the next few lines to make sure the update happens
          const chatSessions = this.#chatSessionService.chatSessions();
          if (chatSessions) {
            this.#chatSessionService.chatSessions.set(chatSessions);
            this.#chatSessionService.selectedSessionId.set(requestBody.id);
          }

          this.dialogRef.close({
            id: this.#id,
            action: this.data.action,
            sensitivityLabel: this.sensitivityLabel.value,
            temperature: this.temperatureControl.value,
            topP: this.topPControl.value,
            model: this.openAiModel.value!,
            isLabelChanged,
            sessionLabel: this.sessionLabel.value,
            agents: requestBody.agents,
            sessionData: resp
          });
        },
        error: (error: any) => {
          this.#alertService.showAlert({
            message: `Failed to update ${this.sessionLabel.value!}.`,
            type: AlertEnum.error,
          });
          this.#spinnerService.hide();
          this.submitting = false;
        },
        complete: () => {
          this.#spinnerService.hide();
          this.submitting = false;
        }
      });
    } else {
      this.#spinnerService.hide();
      return;
    }
  }

  updateErrorMessage() {
    if (this.sessionLabel.hasError('required')) {
      this.errorMessage.set('You must enter a session label.');
    } else {
      this.errorMessage.set('');
    }
  }

  onFilter(value: Event): void {
    const inputValue = value;

    this.gridView = process(this.gridData, {
      filter: {
        logic: "or",
        filters: [
          {
            field: "name",
            operator: "contains",
            value: inputValue,
          },
          {
            field: "description",
            operator: "contains",
            value: inputValue,
          }
        ],
      },
    }).data;

    this.dataBinding()!.skip = 0;
    this.total = this.gridView.length;
  }

  rowCallback = (context: RowClassArgs) => {
    if ((context.index % 2) === 0) {
      return 'even';
    } else {
      return 'odd';
    }
  };

  openNotificationDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: `All chat sessions must now have a Sensitivity Label.  You must select one from the dropdown menu.  The choice you make may reflect which agent you are allowed to use.`,
      buttonLabel: 'Acknowledged',
      title: 'NOTICE',
      showSecondaryButton: false
    };
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }

  /**
   * Updates the Sensitivity Label when a new one is selected
   */
  labelSelected(event: ISensitivityLabelExpanded) {
    this.sensitivityLabel.setValue(event);
    this.sensitivityLabelText = this.#agentService.getSensitivityLabelString(event);
    this.#loadGrantedAgentsBySensitivityLabel(event);
  }


  handleTemperatureChange(event: number): void {
    this.temperature = event;
    this.temperatureControl.setValue(event);
  }

  handleTopPChange(event: number): void {
    this.topP = event;
    this.topPControl.setValue(event);
  }

  modelSelected(event: IOpenAiModel) {
    this.openAiModel.setValue(event);
  }

  isGpt5Model(): boolean {
    return this.#utilitiesService.isGpt5Model(this.openAiModel.value?.model);
  }

  compareLabels(item1: ISensitivityLabelExpanded, item2: ISensitivityLabelExpanded): boolean {
    return item1.id === item2.id;
  }

  #generateGridData(data: IGrantedAgent[]) {
    return data.map((agent: IGrantedAgent) => {
      let sensitivityLabelsList = '';
      if (Object.hasOwn(agent, 'sensitivityLabels')) {
        if (agent.sensitivityLabels && agent.sensitivityLabels.length > 0) {
          sensitivityLabelsList = agent.sensitivityLabels.map((sensitivityLabel: ISensitivityLabel) => this.#agentService.getSensitivityLabelString(sensitivityLabel)).join('; ');
        }
      }

      let owners = '';
      if (Object.hasOwn(agent, 'owners')) {
        if (agent.owners && agent.owners.length > 0) {
          owners = agent.owners.map((owner: any) => owner).join('; ');
        }
      }

      let sensitivityLabels: ISensitivityLabel[] = [];
      if (Object.hasOwn(agent, 'sensitivityLabels')) {
        if (agent.sensitivityLabels && agent.sensitivityLabels.length > 0) {
          sensitivityLabels = agent.sensitivityLabels.map(label => {
            return {
              ...label,
              guid: label.guid ?? ''
            };
          });
        }
      }

      // Add agent to agentSelections if alwaysAvailable is true
      if (agent.alwaysAvailable === true && !this.agentSelections.includes(agent.id)) {
        this.agentSelections.push(agent.id);
      }

      return {
        ...agent,
        ownerList: owners,
        owners: [],
        sensitivityLabels,
        sensitivityLabelList: sensitivityLabelsList,
      };
    });
  }

  /**
   * Filters out any selected agent that may not have the correct Sensitivity Label as the session.
   * Matches by GUID first (backward-compatible with legacy integer id comparison via guid field).
   */
  #filterAgentsWithSensitivityLabelAsSession(sensitivityLabel: ISensitivityLabelExpanded, selectedAgents: string[]): string[] {
    const grantedAgents = this.grantedAgents;
    return grantedAgents
      .filter(agent => selectedAgents.includes(agent.id) && agent.sensitivityLabels &&
        agent.sensitivityLabels.some(label =>
          (label.guid && label.guid === sensitivityLabel.id) ||
          (mapLegacySensitivityLabel(label)?.id === sensitivityLabel.id)
        )
      )
      .map(agent => agent.id);
  }

  #loadGrantedAgentsBySensitivityLabel(sensitivityLabel: ISensitivityLabelExpanded) {
    // sensitivityLabel.id is the canonical code — passed as the API's sensitivityLabel parameter
    this.#apiService.granted(sensitivityLabel?.id).subscribe({
      next: response => {
        this.grantedAgents = response;
        this.gridData = this.#generateGridData(response);
        this.gridView = this.gridData;
        this.total = this.gridView.length;
        // Update selectedKeys based on the new gridView
        this.agentSelections = this.agentSelections.filter(id => this.gridView.some(agent => agent.id === id || agent.alwaysAvailable));
      }
    });
  }
}
